import { logger } from '@accedo/logger';
import { Location } from 'preact-router';
import { LIFECYCLE_EVENT, LifecycleState } from '@accedo/ctv-device-api';
import { PageNavigation } from '##/utils/PageNavigation';
import { StandardCarouselItem } from '##/components/StandardCarousel/types';
import { LiveCarouselItem } from '##/components/LiveCarousel/types';
import { Messengers } from '##/components/Player/utils/Messengers';
import { Options } from '##/utils/Messenger';
import { PlayerStates } from '##/components/Player/PlayerTypes';
import { tealiumTrackError$ } from '##/components/Error';
import { LiveChannel } from '##/services/tenApi/collections/types';
import tenApi from '##/services/tenApi';
import { toLowerWithoutSpaces, isInnovidAd } from '##/utils';
import { MarqueeItem } from '##/components/HomeMarquee/types';
import { isLoggedIn } from '../../authentication/Authentication';
import { sendApiRequest } from '../../tenApi/apirequest';
import { ShowApiResponse } from '../../tenApi/common/types';
import { config } from '../../store/store';
import {
  ClientSideAd,
  BaseDaiAd,
  VodDAIAd,
  VodDAIAdBreak,
  VODPlaybackResponse,
} from '../../tenApi/dai/types';
import { OZTAM_VOD_PUBLISHER_ID } from '../OzTam/OzTamBridge';
import { Tealium, TealiumValues } from './Tealium';
import { TealiumErrorData } from './TealiumErrorTypes';

const NONE_FOUND = 'none-found';
const NA = 'na';
const EXPANDED = 'expand';
const ACCEPT_INVITATION = 'acceptInvitation';

const AD_TYPE = {
  INNOVID: 'video_ad_innovid',
  PAUSE: 'video_ad_pause',
  INTERACTIVE_PAUSE: 'video_ad_intpause',
  PREROLL: 'video_ad_preroll',
  MIDROLL: 'video_ad_midroll',
};

const AD_EVT_TYPE = {
  START: 'ad_start',
  END: 'ad_end',
  SELECT: 'adSelect',
};

const currentEpochTimestamp = (): number => Math.floor(Date.now() / 1000);
type LifecycleEventData = { detail: { state: string; previous: string } };

class TealiumBridge {
  private tealium = new Tealium();
  private prevPathname = window.location.pathname;
  private showValuesCache: TealiumValues | undefined;
  private playerValuesCache: TealiumValues = {};

  private currentAd: ClientSideAd | null = null;
  private hasPlayed = false;
  private vodAdBreaks: VodDAIAdBreak[] = [];
  private waitingForPrerollBeforeSendingContentEvent = false;
  private liveUpdateInterval = -1;

  private currentTime = 0;
  private duration = 0;
  private progressAtLastFrag = 0;
  private isLive = false;
  private comingFromUpNext = false;
  private oztamSessionId = '';
  private deferSendingVideoLoadEvent = false;
  private lastAdStartTime = 0;
  private videoStartTimestamp = 0;
  private videoWatchedDuration = 0;
  private prevLifecycleState: string | null = null;
  private hasSetLivePrerollParameter = false;

  private hasAppExitEventBeenSent = false;

  init = async (): Promise<void> => {
    await this.tealium.init();
    PageNavigation.addRouteChangeListener(this.onRouteChange);
    PageNavigation.subPageRoute$.addReceiver(this.onSubPageNav);
    PageNavigation.appLoaded$.addReceiver(this.onAppLoaded);
    PageNavigation.favouritesButtonPressed$.addReceiver(this.onFavouriteShow);
    PageNavigation.favouriteMoreShowsPressed$.addReceiver(
      this.onFavouriteMoreShows,
    );
    Messengers.playerState$.addReceiver(this.onPlayerStateChange);
    Messengers.currentAd$.addReceiver(this.onAdStateChange);
    Messengers.vodInfo$.addReceiver(this.onVodDAIInfoChange);
    Messengers.userRequestsState$.addReceiver(this.onUserRequestsState);
    Messengers.currentTime$.addReceiver(this.onTimeUpdate);
    Messengers.duration$.addReceiver(this.onDurationChange);
    Messengers.fragChanged$.addReceiver(this.onFragChange);
    Messengers.isLive$.addReceiver(this.onIsLiveChange);
    Messengers.ozTamSessionId$.addReceiver(this.onOztamSessionIdChange);
    Messengers.onMarqueeItemDataFetched$.addReceiver(this.onNavToPlayer);
    tealiumTrackError$.addReceiver(this.onError);
    this.hasAppExitEventBeenSent = false;
  };

  onLifecycleChange = (eventData: unknown): void => {
    const { detail } = (eventData as LifecycleEventData) || {};

    logger.debug(
      `[TealiumBridge] App lifecycle change event state: ${detail?.state}`,
    );

    if (!window.location.pathname.includes('/player')) {
      return;
    }

    if (
      detail?.state === LifecycleState.BACKGROUND &&
      this.prevLifecycleState !== LifecycleState.BACKGROUND
    ) {
      logger.debug(
        '[TealiumBridge] video_exit event fired when app in background state',
      );

      this.onVideoExitEvent(true);
    } else if (
      detail?.state === LifecycleState.FOREGROUND &&
      this.prevLifecycleState !== LifecycleState.FOREGROUND
    ) {
      logger.debug(
        '[TealiumBridge] video_start event fired when app in foreground state',
      );

      this.tealium.onVideoEvent('video_start', {
        ...this.playerValuesCache,
        upNextPlay: String(this.comingFromUpNext),
      });
      this.initVideoStartTimestamp();
    }

    this.prevLifecycleState = detail?.state;
  };

  addOrRemoveLifecycleListener = (add = false): void => {
    window[`${add ? 'add' : 'remove'}EventListener`](
      LIFECYCLE_EVENT,
      this.onLifecycleChange,
    );
  };

  onOztamSessionIdChange = (oztamSessionId: string): void => {
    this.oztamSessionId = oztamSessionId;

    this.playerValuesCache = {
      ...this.playerValuesCache,
      ...(this.oztamSessionId && { oztamSessionId: this.oztamSessionId }),
    };

    if (this.deferSendingVideoLoadEvent) {
      this.sendVideoLoadEvent();

      this.deferSendingVideoLoadEvent = false;
    }
  };

  onActiveGenreChange = (genreFocusEventData: {
    channelGroup: string;
    numberOfChannelGroups: number;
    channelGroupIndex: number;
  }): void => this.tealium.onLiveTvCategoryEvent(genreFocusEventData);

  onCarouselItemClick = (
    carouselItemClickProps:
      | StandardCarouselItem
      | LiveCarouselItem
      | MarqueeItem,
  ): void => this.tealium.onCarouselClickEvent(carouselItemClickProps);

  onAppExit = async (): Promise<void> => {
    if (this.hasAppExitEventBeenSent) {
      return;
    }

    this.hasAppExitEventBeenSent = true;

    if (window.location.pathname.includes('/player')) {
      this.onVideoExitEvent();
    }

    await this.tealium.onExit();
  };

  onFavouriteMoreShows = (): void => {
    this.tealium.onFavouriteMoreShows(window.location.pathname);
  };

  onFavouriteShow = (add = true, options?: Options): void => {
    const eventFn = add ? 'onAddFavourite' : 'onRemoveFavourite';

    this.tealium?.[eventFn]?.(window.location.pathname, options);
  };

  onAddMoreShowsToFavourites = async (): Promise<void> => {
    this.tealium.onFavouriteMoreShows(window.location.pathname);
  };

  onAdSelectEvent = async (
    adTimeout: 'true' | 'false',
    expand = false,
  ): Promise<void> => {
    if (this.currentAd) {
      const adEventValues = this.getAdEventValues(this.currentAd, {
        ...this.playerValuesCache,
        adTimeout,
        adType: AD_TYPE.INNOVID,
        adSelection: expand ? EXPANDED : ACCEPT_INVITATION,
      });

      await this.tealium.onVideoEvent(AD_EVT_TYPE.SELECT, adEventValues);
    }
  };

  private onAppLoaded = (): void => {
    this.onRouteChange({ location: window.location });
  };

  private onRouteChange = async ({
    location: { pathname },
  }: {
    location: Location;
  }): Promise<void> => {
    if (!pathname.includes('/show/')) {
      this.showValuesCache = undefined;
    }
    if (!pathname.includes('/player')) {
      if (this.prevPathname.includes('/player')) {
        this.onVideoExitEvent();
        this.addOrRemoveLifecycleListener();
        this.prevLifecycleState = null;
        clearInterval(this.liveUpdateInterval);
      }
      this.playerValuesCache = {};
      this.deferSendingVideoLoadEvent = false;
      this.hasPlayed = false;
      this.waitingForPrerollBeforeSendingContentEvent = false;
      this.hasSetLivePrerollParameter = false;
      this.currentAd = null;
      this.vodAdBreaks = [];
      this.currentTime = 0;
      this.duration = 0;
      this.comingFromUpNext = false;
      this.progressAtLastFrag = 0;
    }
    if (this.prevPathname === '/sign-in' && isLoggedIn()) {
      let showId;
      const navToPlayer = pathname.includes('/player');
      if (navToPlayer) {
        const episodeState = PageNavigation.getCurrentState() as
          | StandardCarouselItem
          | LiveCarouselItem
          | null;
        if (episodeState) {
          showId =
            (episodeState as StandardCarouselItem).showId ||
            (episodeState as LiveCarouselItem).liveShow?.id;
        }
      }
      const twoPathnamesAgo = PageNavigation.getNthLastPath(2);
      this.tealium.onSignIn(twoPathnamesAgo, pathname, showId);
      if (navToPlayer) {
        this.onNavToPlayer();
      }
    } else if (pathname === '/sign-out') {
      this.tealium.onSignOut(this.prevPathname);
    } else if (
      pathname === '/' ||
      ['/shows', '/news', '/live-tv'].find((path) => pathname.includes(path))
    ) {
      this.tealium.onBrowseEvent(pathname);
    } else if (pathname.includes('/setting')) {
      const additionalOptions: TealiumValues = {
        // because it's the default so we assume it is first
        appSubSection1: 'Change Region',
      };
      this.tealium.onBrowseEvent(pathname, additionalOptions);
    } else if (pathname.includes('/show/')) {
      const routeState = PageNavigation.getCurrentState();
      let showEndpoint = '';
      if (typeof routeState === 'string') {
        showEndpoint = routeState;
      } else if (routeState) {
        showEndpoint = (routeState as { apiEndpoint: string }).apiEndpoint;
      }
      if (showEndpoint) {
        try {
          const response = await sendApiRequest<ShowApiResponse[]>(
            showEndpoint,
          );
          const showResponse = response[0];
          const showValues = {
            showAlias: toLowerWithoutSpaces(showResponse.name),
            showContentFormatType: 'show',
            showEpisodeCount: showResponse.numberOfEpisodes,
            showGenre: showResponse.genre?.label || 'none-found',
            showId: showResponse.id,
            showName: showResponse.name,
            showProgramClassification: showResponse.classification,
            showSeasonCount: showResponse.seasons.length,
            appSubSection1: showResponse.name,
            appSubSection2: 'episodes',
          };
          this.showValuesCache = showValues;
          this.tealium.onBrowseEvent(pathname, showValues);
        } catch (err) {
          logger.info(err);
        }
      }
    } else if (pathname.includes('/player')) {
      if (this.prevPathname.includes('/up-next')) {
        this.comingFromUpNext = true;
      }
      this.onNavToPlayer();
    }
    this.prevPathname = pathname;
  };

  private onSubPageNav = (
    subRoute: string,
    options: { appSubSection3?: string } = {},
  ): void => {
    if (this.prevPathname.includes('/show/')) {
      this.tealium.onBrowseEvent(this.prevPathname, {
        ...this.showValuesCache,
        ...options,
        appSubSection2: subRoute,
      });
    } else {
      this.tealium.onBrowseEvent(this.prevPathname, {
        appSubSection1: subRoute,
      });
    }
  };

  private sendVideoLoadEvent = (): void => {
    this.initVideoStartTimestamp();
    this.tealium.onVideoEvent('video_load', this.playerValuesCache);
  };

  private onNavToPlayer = async (): Promise<void> => {
    const episodeState = PageNavigation.getCurrentState() as
      | StandardCarouselItem
      | LiveCarouselItem
      | MarqueeItem
      | null;
    const isVod = !!(episodeState as StandardCarouselItem)?.showApiEndpoint;
    const isLive = !!(episodeState as LiveCarouselItem)?.liveShow;

    const { ctaEndpoint, ctaTitle, ctaType, ctaId } =
      (episodeState as MarqueeItem) || {};

    const isMarqueeItem = ctaEndpoint || ctaTitle || ctaType || ctaId;

    if (isMarqueeItem && !isLive && !isVod) {
      return;
    }

    if (isVod) {
      const vodEpisode = episodeState as StandardCarouselItem;
      const showEndpoint = vodEpisode.showApiEndpoint;
      try {
        const response = await sendApiRequest<ShowApiResponse[]>(showEndpoint);
        const showResponse = response[0];

        this.playerValuesCache = {
          showAlias: toLowerWithoutSpaces(showResponse.name),
          showChannel: vodEpisode.tvChannel,
          showContentCategory:
            vodEpisode.videoTypeLongForm || vodEpisode.videoTypeShortForm,
          showContentFormatType: vodEpisode.videoTypeLongForm
            ? 'Long Form'
            : 'Short Form',
          showContentId: vodEpisode.showId,
          showContentName: vodEpisode.subtitle,
          showContentSeason: vodEpisode.season,
          showContentVideoId: vodEpisode.id,
          showContentVideoType:
            vodEpisode.videoTypeLongForm || vodEpisode.videoTypeShortForm,
          showGenre: showResponse.genre?.label || 'none-found',
          showId: vodEpisode.id,
          showName: vodEpisode.tvShow,
          showProgramClassification: showResponse.classification,
          videoDuration: vodEpisode.duration,
          oztamPublisherId: OZTAM_VOD_PUBLISHER_ID,
          ...(this.oztamSessionId && { oztamSessionId: this.oztamSessionId }),
        };
        if (this.comingFromUpNext) {
          await this.tealium.onVideoEvent('video_up_next', {
            ...this.playerValuesCache,
            autoPlayFlag: 'enabled',
            upNextshowName: this.playerValuesCache.showName,
            upNextshowContentName: vodEpisode.title,
          });
        }
      } catch (err) {
        logger.info(err);
      }
    } else if (isLive) {
      const liveVideo = episodeState as LiveCarouselItem;
      this.setLivePlayerValues(liveVideo);
      this.liveUpdateInterval = window.setInterval(async () => {
        try {
          const { channels } = await tenApi.collections.getLiveChannels(3);
          const matchingItem = channels?.find(
            (item) => item.name === this.playerValuesCache.showChannel,
          );
          if (matchingItem) {
            this.setLivePlayerValues(matchingItem);
          }
        } catch (err) {}
      }, 60 * 1000);
    } else {
      logger.warn(
        'Tealium: no player info passed, will send empty tracking events',
      );
      this.playerValuesCache = {
        show: 'NONE',
      };
    }

    if (this.oztamSessionId) {
      this.sendVideoLoadEvent();
    } else {
      this.deferSendingVideoLoadEvent = true;
    }

    this.prevLifecycleState = null;
    this.addOrRemoveLifecycleListener(true);
    this.hasSetLivePrerollParameter = false;
  };

  private sendVideoStartOnShowChange = (): void => {
    logger.debug(
      '[TealiumBridge] new live show detected, sending video_start event',
    );

    this.tealium.onVideoEvent('video_start', {
      ...this.playerValuesCache,
      upNextPlay: String(this.comingFromUpNext),
    });
  };

  private setLivePlayerValues = (liveObject: LiveChannel): void => {
    const region = config.$get().geo?.region || '';
    const liveVideo = liveObject;

    const {
      endTime = 0,
      startTime = 0,
      id,
      title = NONE_FOUND,
      genre,
      ratings,
      publisherId,
    } = liveVideo?.liveShow || {};

    const showId = id || NONE_FOUND;

    const hasCurrentLiveShowChanged =
      (this.playerValuesCache.showId &&
        showId !== this.playerValuesCache.showId) ||
      (this.playerValuesCache.showName &&
        title !== this.playerValuesCache.showName);

    this.playerValuesCache = {
      showId,
      showAlias: toLowerWithoutSpaces(title),
      showChannel: liveVideo.name || '',
      showContentFormatType: 'Live',
      showContentId: showId,
      showContentVideoId: showId,
      showGenre: genre || NONE_FOUND,
      showName: title,
      showProgramClassification: ratings || NONE_FOUND,
      showContentName: title || NONE_FOUND,
      isMemberGatedLive: String(!!liveVideo.isMemberGated),
      showContentMarket: region,
      oztamPublisherId: publisherId || NONE_FOUND,
      videoDuration: Math.abs(endTime - startTime) / 60 || NONE_FOUND,
      ...(this.oztamSessionId && { oztamSessionId: this.oztamSessionId }),
    };

    if (hasCurrentLiveShowChanged) {
      if (!this.currentAd) {
        this.sendVideoStartOnShowChange();
      }
    }
  };

  private onPlayerStateChange = async (
    playerState: PlayerStates,
  ): Promise<void> => {
    if (!this.hasPlayed && playerState === PlayerStates.Playing) {
      if (!this.waitingForPrerollBeforeSendingContentEvent) {
        this.hasPlayed = true;
        await this.tealium.onVideoEvent('video_start', {
          ...this.playerValuesCache,
          upNextPlay: String(this.comingFromUpNext),
        });
        await this.tealium.onVideoEvent(
          'video_segment_view',
          this.playerValuesCache,
        );

        this.setLivePrerollParameter(false);
      }
    } else if (playerState === PlayerStates.Ended) {
      if (Messengers.currentAd$.getCurrentValue()) {
        return;
      }

      await this.tealium.onVideoEvent('video_complete', this.playerValuesCache);
    } else if (
      !this.hasPlayed &&
      playerState === PlayerStates.Seeked &&
      this.currentTime > 0
    ) {
      // start time of video is in the middle so no preroll
      this.waitingForPrerollBeforeSendingContentEvent = false;
    }
  };

  private getCreativeAdId = (
    ad: BaseDaiAd | VodDAIAd | ClientSideAd,
  ): string => {
    const creativeId = ad?.creative_ad_id || ad?.ad_id;
    if (creativeId) {
      return creativeId;
    }

    const wrapper = (ad as VodDAIAd)?.wrappers?.find(
      (wrapper) => wrapper?.creative_id || wrapper?.ad_id,
    );

    return wrapper?.creative_id || wrapper?.ad_id || NONE_FOUND;
  };

  private getAdEventValues = (
    ad: BaseDaiAd | ClientSideAd,
    adEventValues: { adType: string } & TealiumValues,
    customAdType?: keyof typeof AD_TYPE,
  ): TealiumValues => {
    const isInnovidAdEvent = ad && isInnovidAd(ad);
    const baseAdTypeStr = isInnovidAdEvent
      ? AD_TYPE.INNOVID
      : adEventValues.adType;

    return {
      ...adEventValues,
      adType: (customAdType && AD_TYPE?.[customAdType]) || baseAdTypeStr,
      adAssetId: this.getCreativeAdId(ad),
      ...(isInnovidAdEvent && {
        adBrandName: NA,
        adCampaign: NA,
        adIndustry: NA,
      }),
    };
  };

  private addToWatchedDurationAndResetStartTimeStamp = (): void => {
    if (this.videoStartTimestamp) {
      this.videoWatchedDuration += Math.max(
        Date.now() - this.videoStartTimestamp,
        0,
      );

      this.videoStartTimestamp = 0;
    }
  };

  private initVideoStartTimestamp = (): void => {
    if (!this.videoStartTimestamp) {
      this.videoStartTimestamp = Date.now();
    }
  };

  private setLivePrerollParameter = (prerollParameter: boolean): void => {
    if (this.hasSetLivePrerollParameter) {
      return;
    }

    this.hasSetLivePrerollParameter = true;

    logger.debug(
      `[TealiumBridge] starting in live ad break set wait for preroll parameter to ${prerollParameter}`,
    );

    this.waitingForPrerollBeforeSendingContentEvent = prerollParameter;
  };

  private onAdStateChange = async (
    currentAd: ClientSideAd | null,
  ): Promise<void> => {
    if (this.isLive && currentAd) {
      this.setLivePrerollParameter(true);
    }

    const previousAd = this.currentAd;
    const adEventValues = {
      adType: this.waitingForPrerollBeforeSendingContentEvent
        ? AD_TYPE.PREROLL
        : AD_TYPE.MIDROLL,
      ...this.playerValuesCache,
    };

    const previousAdEventValues = (previousAd &&
      this.getAdEventValues(previousAd, {
        ...adEventValues,
        adFinishTime: currentEpochTimestamp(),
      })) as TealiumValues;

    const currentAdEventValues = (currentAd &&
      this.getAdEventValues(currentAd, {
        ...adEventValues,
        adStartTime: currentEpochTimestamp(),
      })) as TealiumValues;

    if (previousAd && currentAd) {
      if (this.lastAdStartTime) {
        previousAdEventValues.adWatchedDurationSec =
          currentEpochTimestamp() - this.lastAdStartTime;
        this.lastAdStartTime = 0;
      }
      this.lastAdStartTime = currentAdEventValues.adStartTime as number;
    } else if (previousAd && !currentAd) {
      if (this.lastAdStartTime) {
        previousAdEventValues.adWatchedDurationSec =
          currentEpochTimestamp() - this.lastAdStartTime;
        this.lastAdStartTime = 0;
      }
      this.initVideoStartTimestamp();
      if (this.waitingForPrerollBeforeSendingContentEvent) {
        await this.tealium.onVideoEvent('video_start', {
          ...this.playerValuesCache,
          upNextPlay: String(this.comingFromUpNext),
        });
        this.hasPlayed = true;
        this.waitingForPrerollBeforeSendingContentEvent = false;
      }
      await this.tealium.onVideoEvent(
        'video_segment_view',
        this.playerValuesCache,
      );
    } else if (!previousAd && currentAd) {
      this.addToWatchedDurationAndResetStartTimeStamp();
      this.lastAdStartTime = currentAdEventValues.adStartTime as number;
    }
    this.currentAd = currentAd;
  };

  private onVodDAIInfoChange = (
    newAdInfo: VODPlaybackResponse | null,
  ): void => {
    this.vodAdBreaks = newAdInfo?.ad_breaks || [];
    if (!this.hasPlayed && this.vodAdBreaks[0]?.type === 'pre') {
      this.waitingForPrerollBeforeSendingContentEvent = true;
    }
  };

  private onVideoExitEvent = (fromLifecycleListener = false): void => {
    this.addToWatchedDurationAndResetStartTimeStamp();

    this.tealium.onVideoEvent('video_exit', {
      ...this.playerValuesCache,
      videoWatchedDuration: this.videoWatchedDuration
        ? Math.round(this.videoWatchedDuration / 60000)
        : 0,
    });

    if (!fromLifecycleListener) {
      this.resetVideoTimestampValues();
    }
  };

  private onUserRequestsState = (playerState: PlayerStates): void => {
    if (playerState === PlayerStates.Paused) {
      this.tealium.onVideoEvent('video_pause', this.playerValuesCache);
      this.addToWatchedDurationAndResetStartTimeStamp();
    } else if (playerState === PlayerStates.Seeked) {
      this.tealium.onVideoEvent('video_seeking', this.playerValuesCache);
    } else if (playerState === PlayerStates.Playing) {
      this.initVideoStartTimestamp();
    }
  };

  private resetVideoTimestampValues = (): void => {
    this.videoStartTimestamp = 0;
    this.videoWatchedDuration = 0;
  };

  private onIsLiveChange = (newIsLive: boolean): void => {
    this.isLive = newIsLive;
  };

  private onDurationChange = (newDuration: number): void => {
    this.duration = newDuration;
  };

  private onTimeUpdate = (time: number): void => {
    this.currentTime = time;
  };

  private onFragChange = (): void => {
    if (!this.isLive && this.hasPlayed) {
      // check video progress on frag change, don't need to
      // spam these checks every quarter second
      const currentProgress = this.currentTime / this.duration;
      if (currentProgress >= 0.9 && this.progressAtLastFrag < 0.9) {
        this.tealium.onVideoEvent('video_watched_90', this.playerValuesCache);
      } else if (currentProgress >= 0.5 && this.progressAtLastFrag < 0.5) {
        this.tealium.onVideoEvent('video_watched_50', this.playerValuesCache);
      } else if (currentProgress >= 0.25 && this.progressAtLastFrag < 0.25) {
        this.tealium.onVideoEvent('video_watched_25', this.playerValuesCache);
      }
      this.progressAtLastFrag = currentProgress;
    }
  };

  private onError = ({
    errorCode,
    errorContext,
    errorSource,
    errorDetail,
    errorMessage,
  }: TealiumErrorData): void => {
    const modError = {
      errorCode: `${errorContext}:${errorCode}`,
      errorType: errorContext,
      errorDetail,
      errorSource: errorSource || window.location.pathname,
      ...(errorMessage ? { errorMessage } : {}),
    };
    this.tealium.onErrorEvent(modError);
  };
}
export default new TealiumBridge();
